// OperationLogsViewer — collapsible terminal-style block showing every
// phase output concatenated with phase headers + duration. Used in
// ResultView (collapsed by default) and ErrorView (expanded by default).
//
// Format mirrors `deploy.sh` style — greppable / copyable.

import { Box, Collapse, Text, UnstyledButton } from '@mantine/core'
import { IconChevronRight } from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import { COLOR, SURFACE } from './tokens'
import { PHASE_LABELS } from './types'
import type { DockerOperation, PhaseEntry } from './types'

interface Props {
  operation: DockerOperation
  defaultOpen?: boolean
}

function durationOrDash(entry: PhaseEntry): string {
  if (!entry.started_at) return '—'
  const end = entry.ended_at ? new Date(entry.ended_at) : new Date()
  const ms = end.getTime() - new Date(entry.started_at).getTime()
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.floor((ms % 60_000) / 1000)
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

function statusGlyph(status: PhaseEntry['status']): string {
  switch (status) {
    case 'success':
      return '✓'
    case 'failed':
      return '✗'
    case 'running':
      return '…'
    default:
      return '·'
  }
}

function buildLogText(operation: DockerOperation): string {
  const lines: string[] = []

  lines.push(`# Hard restart — ${operation.container_name.replace(/^\//, '')}`)
  lines.push(`# instance: ${operation.instance_id}`)
  lines.push(`# operation: ${operation.id}`)
  lines.push(`# status: ${operation.status}`)
  if (operation.reason) lines.push(`# reason: ${operation.reason}`)
  lines.push('')

  for (const entry of operation.phases) {
    const label = PHASE_LABELS[entry.name] ?? entry.name
    const glyph = statusGlyph(entry.status)
    const dur = durationOrDash(entry)
    lines.push(`=== ${glyph} ${label} (${dur}) ===`)

    if (entry.error) lines.push(`! ERROR: ${entry.error}`)

    const meta = entry.metadata ?? {}
    if (meta['pull_skipped']) lines.push('! aviso: pull skipado, imagem local em uso')
    if (meta['used_local_image'] && !meta['pull_skipped'])
      lines.push('! aviso: imagem local em uso')

    const out = (entry.output ?? '').trimEnd()
    if (out) {
      lines.push(out)
    } else if (entry.status !== 'running') {
      lines.push('(sem output)')
    }
    lines.push('')
  }

  return lines.join('\n').trimEnd()
}

export function OperationLogsViewer({ operation, defaultOpen }: Props) {
  const [open, setOpen] = useState(!!defaultOpen)
  const logText = useMemo(() => buildLogText(operation), [operation])

  if (operation.phases.length === 0) return null

  return (
    <Box>
      <UnstyledButton
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={open ? 'Recolher logs' : 'Expandir logs'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 0',
        }}
      >
        <motion.span
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.18 }}
          style={{ display: 'inline-flex', color: SURFACE.textSecondary }}
        >
          <IconChevronRight size={14} />
        </motion.span>
        <Text size="xs" c="dimmed">
          {open
            ? 'Ocultar logs da execução'
            : `Ver logs da execução (${logText.length.toLocaleString('pt-BR')} chars)`}
        </Text>
      </UnstyledButton>
      <Collapse in={open}>
        <Box
          component="pre"
          mt={6}
          p={12}
          style={{
            background: COLOR.surfaceDark,
            color: '#c9d1d9',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            borderRadius: 6,
            minHeight: 120,
            maxHeight: 480,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {logText}
        </Box>
      </Collapse>
    </Box>
  )
}
